import images from '@assets/images/index'
import useTable, { ColumnType } from '@components/Table/useTable'
import { Button, ScrollToTopButton } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import {
  renderHeaderTooltip,
  useDropdownMenu
} from '@utils/assessmentReportUtils'
import { Col, Flex, Row, Spin } from 'antd'
import { useMemo, useState } from 'react'

import { Link } from '@suntex.core/nav'
import {
  Database_Procs_GetSchoolPlayers_Item,
  Entities_School_WallOfFame_LevelData
} from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { useFramework } from '@suntex.fim/framework'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'

import SelectWithReact from '../AssessmentReport/SelectWithReact'

import WallOfFameToolTable, {
  columnConfig,
  hidableSelectLabel
} from './WallOfFameToolTable'

/**
 * Wall of Fame Tool Component
 */
function WallOfFameTool() {
  const { api } = useFramework()
  const ALL_LEVELS = 'All Levels'
  const { skillLevel: querySkillLevel } = useAppQueryString()
  const team = useOptionalTeamInfo()
  const { login } = useAuth(isAuthenticated)
  const [filters, setFilters] = useState({
    level: querySkillLevel ?? ALL_LEVELS
  })
  const [isExporting, setIsExporting] = useState(false)

  // Function to generate unique level options from the fetched data
  const generateLevelOptions = (
    levelsData: Entities_School_WallOfFame_LevelData[]
  ) => {
    const levelOptions = levelsData.map((level) => ({
      key: level.header || '',
      label: level.header || ''
    }))

    return Array.from(
      new Map(levelOptions.map((item) => [item.key, item])).values()
    )
  }

  function combineSchoolPlayers(data: Entities_School_WallOfFame_LevelData[]) {
    // flatMap is used to flatten the resulting array after mapping.
    return data.flatMap((item: Entities_School_WallOfFame_LevelData) =>
      item.schoolPlayers?.map(
        (player: Database_Procs_GetSchoolPlayers_Item) => ({
          ...player,
          header: item.header // Add the header to each player
        })
      )
    )
  }

  const { data: wofQueryData, isLoading } = useQuery({
    queryKey: ['school', 'wallOfFame', login.schoolId],
    queryFn: async () => {
      const response = await api.get_School_WallOfFame({
        schoolId: login.schoolId
      })
      return response.levelsData || []
    },
    enabled: login.schoolId > 0
  })

  const wofData = wofQueryData ?? []
  const allData = useMemo(() => combineSchoolPlayers(wofData), [wofData])
  const selectedLevelOptions = useMemo(() => {
    const levelOptions = generateLevelOptions(wofData)
    return [{ key: ALL_LEVELS, label: ALL_LEVELS }, ...levelOptions]
  }, [wofData])

  const selectedWOFData = useMemo(() => {
    if (filters.level === ALL_LEVELS) return null
    return wofData.find((level) => level.header === filters.level) || null
  }, [filters.level, wofData])

  const renderTable = () => {
    if (isLoading) {
      return <Spin />
    }

    if (filters.level === ALL_LEVELS) {
      return wofData.map((level) => (
        <WallOfFameToolTable
          key={level.header}
          levelsData={level}
          isLoading={isLoading}
          team={team}
        />
      ))
    }

    if (selectedWOFData) {
      return (
        <WallOfFameToolTable
          key={selectedWOFData.header}
          levelsData={selectedWOFData}
          isLoading={isLoading}
          team={team}
        />
      )
    }

    return <p>No data available for the selected level.</p>
  }
  // Column configuration for the table
  const columnConfigAll = (): ColumnType<any>[] => [
    {
      header: () => renderHeaderTooltip('Skill Level'),
      id: 'header',
      accessorKey: 'header',
      size: 50,
      enableSorting: false
    },
    ...columnConfig()
  ]
  // Memoize column configuration
  const columns = useMemo(() => columnConfigAll(), [])
  // Label configuration for hidden columns when exporting
  const hidableSelectLabelAll: Record<string, string> = {
    header: 'Skill Level',
    ...hidableSelectLabel
  }
  // Initialize table with data, columns, and loading state
  const { table } = useTable({
    data: allData || [],
    columns,
    isLoading: isLoading || isExporting
  })

  const dropdownItems = useDropdownMenu({
    table,
    hidableSelectLabel: hidableSelectLabelAll,
    fileName: 'WallofFame',
    setIsLoading: setIsExporting
  })
  return (
    <Flex vertical gap="20px" style={{ position: 'relative' }}>
      {/* Loader overlay when loading and popup not open */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      <Row gutter={10} className="main-navigation">
        <Col>
          <Flex gap="10px" className="main-navigation__heading" align="center">
            <h3>Wall of Fame</h3>
          </Flex>
          <div className="main-navigation__stats">
            <div className="stat stat-info">
              <Flex gap="6px" className="label" align="center">
                <span>
                  <images.InfoCircleSm />
                </span>
                Highlighted rows indicate players who went up a skill level in
                the prior week.
              </Flex>
            </div>
          </div>
        </Col>
        <Col flex={1} className={'main-navigation__btn-container print-hidden'}>
          <Flex style={{ width: '100%' }} justify="flex-end" gap={15}>
            <Link
              to="https://explore.firstinmath.com/wall-of-fame"
              className="text-body"
              target="_blank"
            >
              <Button intent="secondary" style={{ padding: '10px 16px' }}>
                Display Instructions
              </Button>
            </Link>
          </Flex>
        </Col>
      </Row>

      <div className="main-result">
        <Row className="main-result__toolbar">
          <Col span={8} className="report-options">
            <p className="dd-label fw-bold text-lg">Skill Level</p>
            <div className="select-dropdown-fluency wall-of-fame-select">
              <SelectWithReact
                value={filters.level}
                onChange={(level: string) => setFilters({ level })}
                options={selectedLevelOptions}
              />
            </div>
          </Col>
        </Row>

        {/* Render the Wall of Fame Table */}
        {renderTable()}
      </div>
      <ScrollToTopButton />
    </Flex>
  )
}

export default WallOfFameTool
